import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, X, PlayCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTutorial } from '@/hooks/useTutorials';
import { useTutorialModal } from '@/contexts/TutorialModalContext';
import { appConfig } from '@/config/app.config';
import TutorialHeader from './TutorialHeader';
import TutorialContent from './TutorialContent';
import TutorialMedia from './TutorialMedia';
import TutorialSteps from './TutorialSteps';

// TutorialViewer - Componente Universal para visualizar tutoriais
const TutorialViewer = ({ slug, viewMode = 'full', focusStepId = null }: { slug: string, viewMode?: 'full' | 'step', focusStepId?: string | number | null }) => {
  const { switchToFullView } = useTutorialModal();
  const { data, isLoading, error } = useTutorial(slug) as any;
  const [focusedStepIndex, setFocusedStepIndex] = useState<number | null>(null);
  const [activeStepId, setActiveStepId] = useState('overview');

  // High-Fidelity Mock for immediate demonstration
  const tutorialMock = useMemo(() => ({
    title: "Bem-vindo ao Ecossistema Lukos",
    description: "Inicie sua jornada agora e descubra como as soluções Lukos vão transformar a gestão do seu negócio.",
    content: "Este Onboarding foi desenhado para apresentar os pilares da tecnologia Lukos e como você pode extrair o máximo de performance da nossa plataforma.",
    category: { name: "Onboarding" },
    tutorialSteps: [
      { 
        id: "1", sortOrder: 1, 
        title: "Sua Nova Central de Inteligência", 
        content: "A Lukos não é apenas um software, é o cérebro da sua operação. Aqui você terá visão 360º de vendas, estoque e financeiro em uma interface boutique.", 
        imageUrl: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200" 
      },
      { 
        id: "2", sortOrder: 2, 
        title: "Módulos que Conversam entre si", 
        content: "Do PDV à Conciliação Bancária, tudo é nativamente integrado. O que acontece na pista reflete instantaneamente no seu Dashboard de gestão.", 
        imageUrl: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200" 
      },
      { 
        id: "3", sortOrder: 3, 
        title: "Aprenda no seu Ritmo", 
        content: "Nossos tutoriais interativos permitem que você treine sua equipe sem custos externos. Vídeos, screenshots e passos claros garantem o domínio total do sistema.", 
        imageUrl: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=1200" 
      },
      { 
        id: "4", sortOrder: 4, 
        title: "Suporte quando você precisar", 
        content: "A LUK, nossa IA, está disponível 24/7 para tirar dúvidas. Além disso, nossa equipe de suporte boutique está a um clique de distância para suporte estratégico.", 
        imageUrl: "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&q=80&w=1200" 
      }
    ]
  }), []);

  const tutorial = data?.data || (['demo', 'onboarding'].includes(slug) ? tutorialMock : null);

  // Normalizar os passos do tutorial
  const tutorialSteps = tutorial
    ? (tutorial.tutorialSteps || tutorial.TutorialSteps || tutorial.steps || tutorial.Steps || [])
    : [];

  useEffect(() => {
    if (tutorialSteps.length > 0 && activeStepId === 'overview') {
      // Keep overview as default
    }
  }, [tutorialSteps]);

  if (isLoading && slug !== 'demo' && slug !== 'onboarding') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[600px] text-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
        <div className="text-xl text-purple-950 font-black uppercase tracking-widest italic">Sincronizando Stage...</div>
      </div>
    );
  }

  if (!tutorial && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center gap-6 bg-slate-50 p-20 rounded-[3rem]">
        <div className="w-20 h-20 bg-slate-200 rounded-full flex items-center justify-center text-slate-400">
           <X size={40} />
        </div>
        <div className="space-y-2">
           <h2 className="text-3xl font-black text-slate-900 tracking-tighter">Tutorial não localizado</h2>
           <p className="text-slate-500 font-medium">O recurso solicitado no slug <span className="text-purple-600 font-bold">"{slug}"</span> ainda não foi mapeado.</p>
        </div>
        <button onClick={() => window.history.back()} className="px-8 py-3 bg-slate-950 text-white rounded-2xl font-black uppercase tracking-widest text-[11px]">Voltar para Galeria</button>
      </div>
    );
  }

  // Modo completo (padrão) - Agora no formato de Player Passo-a-Passo
  const currentStep = tutorialSteps.find((s: any) => String(s.id || s.Id) === activeStepId);

  const handleNavigatePlayer = (direction: 'next' | 'prev') => {
    if (direction === 'next' && currentStep) {
      const idx = tutorialSteps.indexOf(currentStep);
      if (idx < tutorialSteps.length - 1) {
        const next = tutorialSteps[idx + 1];
        setActiveStepId(String(next.id || next.Id));
      }
    } else if (direction === 'prev' && currentStep) {
      const idx = tutorialSteps.indexOf(currentStep);
      if (idx > 0) {
        const prev = tutorialSteps[idx - 1];
        setActiveStepId(String(prev.id || prev.Id));
      } else {
        setActiveStepId('overview');
      }
    }
  };

  return (
    <div className="w-full h-full min-h-screen bg-white">
      <AnimatePresence mode="wait">
        <motion.div
           key={activeStepId}
           initial={{ opacity: 0, y: 10 }}
           animate={{ opacity: 1, y: 0 }}
           exit={{ opacity: 0, y: -10 }}
           transition={{ duration: 0.4, ease: "easeOut" }}
           className="w-full h-full"
        >
          {activeStepId === 'overview' ? (
            <div className="max-w-7xl mx-auto px-8 py-20 lg:py-32">
              
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center mb-24">
                 <div className="lg:col-span-7 space-y-8">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-50 text-purple-600 border border-purple-100 font-black text-[10px] uppercase tracking-[0.2em]">
                       <PlayCircle size={14} /> Tutorial em Alta Definição
                    </div>
                    <h1 className="text-6xl lg:text-8xl font-black text-slate-950 tracking-tighter leading-[0.9]">
                       {tutorial.title || tutorial.Title}
                    </h1>
                    <p className="text-slate-500 font-medium text-lg lg:text-xl leading-relaxed max-w-2xl">
                       {tutorial.description || tutorial.Description}
                    </p>
                    <div className="flex items-center gap-6 pt-4">
                       <button
                         onClick={() => setActiveStepId(String(tutorialSteps[0].id || tutorialSteps[0].Id))}
                         className="h-16 px-12 bg-slate-950 text-white rounded-3xl font-black uppercase tracking-widest text-[11px] flex items-center gap-4 hover:scale-105 transition-all shadow-2xl shadow-purple-500/20"
                       >
                          Começar Experiência <ArrowRight size={20} />
                       </button>
                    </div>
                 </div>

                 <div className="lg:col-span-5 relative group">
                    <div className="absolute -inset-4 bg-linear-to-br from-purple-500 to-indigo-500 opacity-20 blur-3xl rounded-full scale-75 group-hover:scale-100 transition-transform duration-1000" />
                    <div className="relative aspect-4/5 rounded-[3.5rem] overflow-hidden border border-white shadow-2xl">
                       <img src={tutorialSteps[0]?.imageUrl || "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800"} className="w-full h-full object-cover" />
                       <div className="absolute inset-0 bg-linear-to-t from-slate-950 via-transparent to-transparent" />
                       <div className="absolute bottom-10 left-10">
                          <span className="text-white/60 text-[10px] font-black uppercase tracking-widest">Amostra da Aula</span>
                          <p className="text-white font-bold text-xl uppercase tracking-tighter">Primeiros Passos</p>
                       </div>
                    </div>
                 </div>
              </div>

              {/* Dynamic Content Details */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 pt-24 border-t border-slate-100">
                 <div className="space-y-4">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-purple-600">Requisitos</h4>
                    <p className="text-slate-600 font-medium text-sm leading-relaxed">Conexão estável e acesso ao módulo de retaguarda habilitado no perfil do usuário.</p>
                 </div>
                 <div className="space-y-4">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-purple-600">O que você vai aprender</h4>
                    <ul className="text-slate-600 font-medium text-sm space-y-2">
                       {tutorialSteps.map((s: any, i: number) => (
                          <li key={i} className="flex items-center gap-3">
                             <div className="w-1.5 h-1.5 rounded-full bg-slate-300" /> {s.title}
                          </li>
                       ))}
                    </ul>
                 </div>
                 <div className="space-y-4">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-purple-600">Certificação</h4>
                    <p className="text-slate-600 font-medium text-sm leading-relaxed">Ao concluir este tutorial, você estará habilitado para operar este módulo com segurança.</p>
                 </div>
              </div>

            </div>
          ) : (
            <div className="w-full h-full">
              {currentStep && (
                <TutorialSteps
                  steps={tutorialSteps}
                  tutorialTitle={tutorial.title || tutorial.Title}
                  activeStepId={activeStepId}
                  isPlayerMode={true}
                  onNextStep={handleNavigatePlayer}
                  totalSteps={tutorialSteps.length}
                  currentIndex={tutorialSteps.indexOf(currentStep)}
                  onStepClick={(id: string) => setActiveStepId(String(id))}
                  onBackToOverview={() => setActiveStepId('overview')}
                />
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default TutorialViewer;
